import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { User } from '../../api/accessControl/User.ts';
import { DocumentDrawer } from './DocumentDrawer.tsx';
import { DocumentDrawerDisplayMode, DrawerAutoAdvanceTrigger, DEFAULT_AUTO_ADVANCE } from './types/drawer.ts';
import type {
    DrawerNavigation,
    DrawerSession,
    OpenDocumentOptions,
    DrawerDocument,
} from './types/drawer.ts';

export type DocumentHandoff = {
    documentId: string;
    trigger: DrawerAutoAdvanceTrigger;
};

export type DocumentChange = {
    documentId: string;
};

export type DocumentDrawerActions = {
    open: (businessId: string, documentId: string, options?: OpenDocumentOptions) => void;
    close: () => void;
    documentHandled: (documentId: string, trigger: DrawerAutoAdvanceTrigger) => void;
    finishHandoff: () => void;
    notifyDocumentChanged: (documentId: string) => void;
};

type DocumentDrawerState = {
    session: DrawerSession | null;
    navigation: DrawerNavigation | null;
    handoff: DocumentHandoff | null;
    documentChanged: DocumentChange | null;
};

const HANDLED_DOCUMENT_LEAVES_LIST: Record<DrawerAutoAdvanceTrigger, boolean> = {
    [DrawerAutoAdvanceTrigger.Delete]: true,
    [DrawerAutoAdvanceTrigger.Save]: true,
    [DrawerAutoAdvanceTrigger.Recategorize]: false,
    [DrawerAutoAdvanceTrigger.Archive]: true,
    [DrawerAutoAdvanceTrigger.MarkAsCreditCardStatement]: true,
};

function advancePast(session: DrawerSession | null, handoff: DocumentHandoff): DrawerSession | null {
    if (!session) {
        return null;
    }
    const handledIndex = session.documentList.findIndex((doc) => doc.id === handoff.documentId);
    const handledDocumentLeavesList = HANDLED_DOCUMENT_LEAVES_LIST[handoff.trigger];
    const documentList = handledDocumentLeavesList
        ? session.documentList.filter((doc) => doc.id !== handoff.documentId)
        : session.documentList;

    if (session.documentId !== handoff.documentId) {
        return handledIndex < 0 ? session : { ...session, documentList };
    }
    if (!session.autoAdvance[handoff.trigger]) {
        return null;
    }
    if (handledIndex < 0) {
        return null;
    }
    const next = handledDocumentLeavesList
        ? documentList[Math.min(handledIndex, documentList.length - 1)]
        : documentList[handledIndex + 1];
    if (!next) {
        return null;
    }
    return {
        ...session,
        businessId: next.businessId,
        documentId: next.id,
        documentList,
        documentDrawerOptions: next.documentDrawerOptions,
        initialTab: undefined,
    };
}

const DocumentDrawerActionsContext = createContext<DocumentDrawerActions | null>(null);
const DocumentDrawerStateContext = createContext<DocumentDrawerState>({
    session: null,
    navigation: null,
    handoff: null,
    documentChanged: null,
});

export function useOptionalDocumentDrawer(): DocumentDrawerActions | null {
    return useContext(DocumentDrawerActionsContext);
}

export function useDocumentDrawer(): DocumentDrawerActions {
    const actions = useOptionalDocumentDrawer();
    if (!actions) {
        throw new Error(
            'useDocumentDrawer must be used inside a <DocumentDrawerProvider>. Wrap the route content in Routes.tsx.',
        );
    }
    return actions;
}

export function useOpenDocument(): DrawerSession | null {
    return useContext(DocumentDrawerStateContext).session;
}

export function useDrawerNavigation(): DrawerNavigation | null {
    return useContext(DocumentDrawerStateContext).navigation;
}

export function usePendingHandoff(): DocumentHandoff | null {
    return useContext(DocumentDrawerStateContext).handoff;
}

export function useDocumentChanged(): DocumentChange | null {
    return useContext(DocumentDrawerStateContext).documentChanged;
}

export function DocumentDrawerProvider({
    currentUser,
    layerClassName,
    children,
}: {
    currentUser: User;
    layerClassName?: string;
    children: ReactNode;
}) {
    const [session, setSession] = useState<DrawerSession | null>(null);
    const onCloseRef = useRef<(() => void) | undefined>(undefined);
    const [handoff, setHandoff] = useState<DocumentHandoff | null>(null);
    const [documentChanged, setDocumentChanged] = useState<DocumentChange | null>(null);
    const pendingHandoff = useRef<DocumentHandoff | null>(null);

    const open = useCallback(
        (businessId: string, documentId: string, options: OpenDocumentOptions = {}) => {
            const documentList = options.documentList ?? [];
            onCloseRef.current = options.onClose;
            setSession({
                businessId,
                documentId,
                documentList,
                documentDrawerOptions:
                    options.documentDrawerOptions ??
                    documentList.find((doc) => doc.id === documentId)?.documentDrawerOptions,
                initialTab: options.initialTab,
                displayMode: options.displayMode ?? DocumentDrawerDisplayMode.FollowReason,
                autoAdvance: { ...DEFAULT_AUTO_ADVANCE, ...options.autoAdvance },
            });
        },
        [],
    );

    const close = useCallback(() => {
        const onClose = onCloseRef.current;
        onCloseRef.current = undefined;
        setSession(null);
        onClose?.();
    }, []);

    const documentHandled = useCallback(
        (documentId: string, trigger: DrawerAutoAdvanceTrigger) => {
            pendingHandoff.current = { documentId, trigger };
            setHandoff(pendingHandoff.current);
        },
        [],
    );

    const finishHandoff = useCallback(() => {
        const finished = pendingHandoff.current;
        pendingHandoff.current = null;
        setHandoff(null);
        if (finished) {
            setSession((previous) => advancePast(previous, finished));
        }
    }, []);

    const notifyDocumentChanged = useCallback(
        (documentId: string) => setDocumentChanged({ documentId }),
        [],
    );

    const actions = useMemo(
        () => ({ open, close, documentHandled, finishHandoff, notifyDocumentChanged }),
        [open, close, documentHandled, finishHandoff, notifyDocumentChanged],
    );

    const navigation = useMemo<DrawerNavigation | null>(() => {
        if (!session || session.documentList.length < 2) {
            return null;
        }
        const currentIndex = session.documentList.findIndex((doc) => doc.id === session.documentId);
        if (currentIndex < 0) {
            return null;
        }
        const step = (target: DrawerDocument | undefined) => () => {
            if (!target) {
                return;
            }
            setSession((previous) =>
                previous
                    ? {
                          ...previous,
                          businessId: target.businessId,
                          documentId: target.id,
                          documentDrawerOptions: target.documentDrawerOptions,
                          initialTab: undefined,
                          displayMode: previous.displayMode,
                      }
                    : previous,
            );
        };
        return {
            total: session.documentList.length,
            currentIndex,
            fileName: session.documentList[currentIndex].fileName,
            onPrevious: step(session.documentList[currentIndex - 1]),
            onNext: step(session.documentList[currentIndex + 1]),
        };
    }, [session]);

    const state = useMemo(
        () => ({ session, navigation, handoff, documentChanged }),
        [session, navigation, handoff, documentChanged],
    );

    return (
        <DocumentDrawerActionsContext.Provider value={actions}>
            <DocumentDrawerStateContext.Provider value={state}>
                {children}
                <DocumentDrawer currentUser={currentUser} layerClassName={layerClassName} />
            </DocumentDrawerStateContext.Provider>
        </DocumentDrawerActionsContext.Provider>
    );
}
